// Single source of truth for product-analytics event names.
//
// Keep names snake_case and grouped by domain prefix. Importing from here
// (instead of inlining string literals at call sites) prevents name drift,
// which is the #1 cause of unusable analytics data.
//
// Phase 1 scope:
//   Activation funnel: signup_succeeded -> email_verified -> onboarding_completed
//     -> tool_card_clicked -> board_generation_run -> image_downloaded
//   Conversion funnel: pricing_page_viewed -> plan_selected -> checkout_started
//     -> checkout_returned_success (checkout_returned_cancel = drop-off)
// Phase 3 scope:
//   Secondary engagement (board_created, home_intention_clicked, image_shared,
//     remix_initiated, result_favorited, model_training_*,
//     team_member_invited, referral_link_copied, feature_tour_*)
//   Frustration / stuck (generation_failed, help_center_viewed,
//     support_dialog_opened, support_chat_open_*, feature_tour_dismissed)
// Later phases append to this map.

export const EVENTS = {
	// Global Search (cmd+K modal) — privacy: NEVER include raw query text,
	// prompts, titles, or URLs in properties (a query's length is sent, never
	// its text).
	SEARCH_OPENED: 'search_opened',
	SEARCH_PERFORMED: 'search_performed',
	SEARCH_RESULT_CLICKED: 'search_result_clicked',
	SEARCH_ZERO_RESULTS: 'search_zero_results',
	SEARCH_RECENT_CLICKED: 'search_recent_clicked',
	SEARCH_VIEW_ALL_CLICKED: 'search_view_all_clicked',

  // Acquisition / signup
  SIGNUP_SUCCEEDED: 'signup_succeeded',
  EMAIL_VERIFIED: 'email_verified',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  // The Boards v2 tour (IL-095): reason (new-account, first-board, replay) and, skipped, the step it was on
  BOARDS_TOUR_STARTED: 'boards_tour_started',
  BOARDS_TOUR_COMPLETED: 'boards_tour_completed',
  BOARDS_TOUR_SKIPPED: 'boards_tour_skipped',

  // Core product / activation
  TOOL_CARD_CLICKED: 'tool_card_clicked',
  BOARD_GENERATION_RUN: 'board_generation_run',
  IMAGE_DOWNLOADED: 'image_downloaded',

  // Conversion / revenue
  PRICING_PAGE_VIEWED: 'pricing_page_viewed',
  PLAN_SELECTED: 'plan_selected',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_RETURNED_SUCCESS: 'checkout_returned_success',
  CHECKOUT_RETURNED_CANCEL: 'checkout_returned_cancel',

  // Phase 3 — Core product secondary
  HOME_INTENTION_CLICKED: 'home_intention_clicked',
  BOARD_CREATED: 'board_created',
  IMAGE_SHARED: 'image_shared',
  REMIX_INITIATED: 'remix_initiated',
  RESULT_FAVORITED: 'result_favorited',

  // Phase 3 — Engagement
  MODEL_TRAINING_STARTED: 'model_training_started',
  MODEL_USED_AS_TOOL: 'model_used_as_tool',
  TEAM_MEMBER_INVITED: 'team_member_invited',
  REFERRAL_LINK_COPIED: 'referral_link_copied',

  // Phase 3 — Feature tours
  FEATURE_TOUR_STARTED: 'feature_tour_started',
  FEATURE_TOUR_COMPLETED: 'feature_tour_completed',
  FEATURE_TOUR_DISMISSED: 'feature_tour_dismissed',

  // Phase 3 — Frustration / stuck
  GENERATION_FAILED: 'generation_failed',
  HELP_CENTER_VIEWED: 'help_center_viewed',
  SUPPORT_DIALOG_OPENED: 'support_dialog_opened',
  // Support chat open funnel. Every attempt emits ATTEMPTED and resolves to
  // exactly one of SUCCEEDED / FAILED, so the failure rate is a ratio rather
  // than a raw count. FAILED carries `failure_reason` from
  // SUPPORT_CHAT_FAILURE, which is what separates "their firewall blocked
  // HubSpot" from "our token call failed" — the two were indistinguishable
  // before, and both rendered the same sentence blaming the user's network.
  SUPPORT_CHAT_OPEN_ATTEMPTED: 'support_chat_open_attempted',
  SUPPORT_CHAT_OPEN_SUCCEEDED: 'support_chat_open_succeeded',
  SUPPORT_CHAT_OPEN_FAILED: 'support_chat_open_failed',
  // Saving an annotation fails client-side (canvas compositing) or at upload.
  // Carries `reason` from ANNOTATION_SAVE_FAILURE plus image dimensions and
  // blob size, so a support report can be diagnosed without a repro.
  ANNOTATION_SAVE_FAILED: 'annotation_save_failed',
} as const;

export type AnalyticsEvent = typeof EVENTS[keyof typeof EVENTS];

// Cross-surface taxonomy shared with the plugins. Re-exported here so web call
// sites and every plugin draw event names + super-property keys from ONE
// source (shared/src/analytics) — no copied literals across surfaces. Web's
// own EVENTS above stay web-specific (board_generation_run, signup_*, …);
// these are the shared events + the surface discriminator. See ADR-0016.
// Imported from shared's main entry (not the `shared/analytics` subpath):
// web's webpack resolves the package main, while plugins use the deps-free
// subpath directly. Both reach the same constants.
export { SHARED_EVENTS, SURFACES, SUPER_PROPS } from '@run-diffusion/shared';
export type { Surface, SuperPropKey, SharedEvent } from '@run-diffusion/shared';
